import { describe, it, expect } from '@jest/globals';
import { mergePackageSettings } from './mergePackageSettings.js';
import { alwaysExcludeGlobs, sourceFilesGlobs } from '@ms-cloudpack/path-utilities';

describe('mergePackageSettings', () => {
  it('merges exports maps', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, exports: { './foo': './src/foo.js' } },
      { match: { name: 'test' }, exports: { './bar': './src/bar.js' } },
      { match: { name: 'test' }, exports: ['./src/baz.js'] },
    ]);
    expect(result.exports).toEqual([{ './foo': './src/foo.js' }, { './bar': './src/bar.js' }, './src/baz.js']);
  });

  it('merges included dependencies', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, includedDependencies: ['foo'] },
      { match: { name: 'test' }, includedDependencies: ['bar'] },
    ]);
    expect(result.includedDependencies).toEqual(['foo', 'bar']);
  });

  it('merges excluded dependencies', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, excludedDependencies: ['foo'] },
      { match: { name: 'test' }, excludedDependencies: ['bar'] },
    ]);
    expect(result.excludedDependencies).toEqual(['foo', 'bar']);
  });

  it('merges inlined dependencies', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, inlinedDependencies: ['foo'] },
      { match: { name: 'test' }, inlinedDependencies: ['bar'] },
    ]);
    expect(result.inlinedDependencies).toEqual(['foo', 'bar']);
  });

  it('overwrites bundler', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, bundler: 'webpack' },
      { match: { name: 'test' }, bundler: 'rollup' },
    ]);
    expect(result.bundler).toEqual('rollup');
  });

  it('replaces bundler options', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, bundlerOptions: { foo: 'foo' } },
      { match: { name: 'test' }, bundlerOptions: { bar: 'bar' } },
    ]);
    expect(result.bundlerOptions).toEqual({ bar: 'bar' });
  });

  it('merges input paths', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, inputPaths: ['foo'] },
      { match: { name: 'test' }, inputPaths: ['bar'] },
    ]);
    expect(result.inputPaths).toEqual(['foo', 'bar', ...alwaysExcludeGlobs]);
  });

  it('does not duplicate default input paths', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, inputPaths: ['...'] },
      { match: { name: 'test' }, inputPaths: ['...'] },
    ]);
    expect(result.inputPaths).toEqual([...sourceFilesGlobs]);
  });

  it('dedupes input paths', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, inputPaths: ['foo', '...'] },
      { match: { name: 'test' }, inputPaths: ['...', 'foo', 'bar'] },
    ]);
    expect(result.inputPaths).toEqual(['foo', ...sourceFilesGlobs, 'bar']);
  });

  it('merges link settings', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, link: { duplicatedDependencyBehavior: 'allow-duplication' } },
      { match: { name: 'test' }, link: { duplicatedDependencyBehavior: 'force-host-version' } },
    ]);
    expect(result.link?.duplicatedDependencyBehavior).toEqual('force-host-version');
  });

  it('replaces `...` to default input paths', () => {
    const result = mergePackageSettings([
      { match: { name: 'test' }, inputPaths: ['foo'] },
      { match: { name: 'test' }, inputPaths: ['...'] },
    ]);
    expect(result.inputPaths).toEqual(['foo', ...sourceFilesGlobs]);
  });

  it('returns an empty object if no settings are provided', () => {
    const result = mergePackageSettings([]);
    expect(result).toEqual({});
  });

  it('handled both object and string matchers', () => {
    const result = mergePackageSettings([
      {
        match: 'string',
        ignoredBundlerWarnings: ['The "??" operator here will always return the left operand'],
      },
      {
        match: {
          name: 'string',
        },
        bundler: 'rollup',
      },
    ]);
    expect(result).toEqual({
      ignoredBundlerWarnings: ['The "??" operator here will always return the left operand'],
      bundler: 'rollup',
    });
  });
});
